import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useOwnerLayoutData } from '@/hooks/useOwnerLayoutData';
import { APP_CONFIG } from '@/config/app.config';
import { cn } from '@/lib/utils';
import ApperIcon from '@/components/ApperIcon';
import UserMenu from '@/components/UserMenu';
import { ErrorBoundary } from '@/components/ui/error-boundary';

export default function OwnerLayout() {
  const { user, logout, homeRoute, navItems, navGroups, initials, displayName } = useOwnerLayoutData();
  const [navOpen, setNavOpen] = useState(false);
  const groups = navGroups.length ? navGroups : [{ label: null, items: navItems }];
  const sidebarNav = groups.map((group, groupIndex) => (
    <section key={group.label || groupIndex} className="space-y-2">
      {group.label && <p className="px-3 pt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-sidebar-foreground/55">{group.label}</p>}
      {group.items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={() => setNavOpen(false)} className={({ isActive }) => cn('flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm transition-colors', isActive ? 'bg-sidebar-primary text-sidebar-primary-foreground' : 'text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground')}>
          <ApperIcon name={item.icon || 'Circle'} size={18} /><span>{item.label}</span>
        </NavLink>
      ))}
    </section>
  ));

  return (
    <div className="min-h-svh bg-background text-foreground md:grid md:grid-cols-[256px_minmax(0,1fr)]">
      <aside className="hidden min-h-svh flex-col border-r border-sidebar-border bg-sidebar px-4 py-5 md:flex">
        <Link to={homeRoute} className="flex items-center gap-3 px-2">
          <span className="flex size-10 items-center justify-center rounded-full bg-sidebar-primary text-sidebar-primary-foreground"><ApperIcon name={APP_CONFIG.icon} size={20} /></span>
          <span className="min-w-0"><span className="block truncate font-heading text-lg font-semibold text-sidebar-foreground">{APP_CONFIG.name}</span><span className="block text-xs text-sidebar-foreground/60">Owner workspace</span></span>
        </Link>
        <nav aria-label="Store navigation" className="mt-8 flex-1 space-y-5">{sidebarNav}</nav>
        <div className="border-t border-sidebar-border pt-4"><UserMenu user={user} initials={initials} displayName={displayName} logout={logout} /></div>
      </aside>
      <div className="min-w-0">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur md:hidden">
          <button type="button" className="flex size-11 items-center justify-center rounded-lg hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={navOpen ? 'Close store navigation' : 'Open store navigation'} aria-expanded={navOpen} onClick={() => setNavOpen((open) => !open)}><ApperIcon name={navOpen ? 'X' : 'Menu'} size={20} /></button>
          <Link to={homeRoute} className="font-heading text-lg font-semibold">{APP_CONFIG.name}</Link>
          <div className="ml-auto"><UserMenu user={user} initials={initials} displayName={displayName} logout={logout} /></div>
        </header>
        {navOpen && <nav aria-label="Store navigation" className="fixed inset-x-0 top-14 z-30 border-b border-border bg-sidebar px-4 py-4 shadow-lg md:hidden">{sidebarNav}</nav>}
        <main className="mx-auto w-full max-w-[1440px] p-4 sm:p-6 lg:p-8"><ErrorBoundary><Outlet /></ErrorBoundary></main>
      </div>
    </div>
  );
}